import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import ChartControls from './components/ChartControls';
import PriceChart from './components/PriceChart';
import CorrelationPanel from './components/CorrelationPanel';
import AdvancedFilters from './components/AdvancedFilters';
import ScenarioModeling from './components/ScenarioModeling';
import AIInsightsPanel from './components/AIInsightsPanel';
import AssetSelector from './components/AssetSelector';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const MarketAnalysis = () => {
  const [selectedAsset, setSelectedAsset] = useState('EUA');
  const [selectedContract, setSelectedContract] = useState('DEC24');
  const [selectedTimeframe, setSelectedTimeframe] = useState('1d');
  const [activeIndicators, setActiveIndicators] = useState(['sma', 'volume']);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [activeView, setActiveView] = useState('overview');
  const [filters, setFilters] = useState({});

  useEffect(() => {
    document.title = 'Market Analysis - TradePulse Pro';
  }, []);

  const handleIndicatorToggle = (indicatorId) => {
    setActiveIndicators(prev => 
      prev?.includes(indicatorId) 
        ? prev?.filter(id => id !== indicatorId)
        : [...prev, indicatorId]
    );
  };

  const handleDrawingToolSelect = (toolId) => {
    console.log('Drawing tool selected:', toolId);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const viewOptions = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'correlation', label: 'Correlation', icon: 'GitBranch' },
    { id: 'scenario', label: 'Scenario', icon: 'Target' },
    { id: 'insights', label: 'AI Insights', icon: 'Brain' }
  ];

  const renderMainContent = () => {
    switch (activeView) {
      case 'correlation':
        return (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <CorrelationPanel />
            </div>
            <div>
              <AIInsightsPanel />
            </div>
          </div>
        );
      case 'scenario':
        return (
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            <div className="xl:col-span-3">
              <ScenarioModeling />
            </div>
            <div>
              <AIInsightsPanel />
            </div>
          </div>
        );
      case 'insights':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AIInsightsPanel />
            <div className="space-y-6">
              <CorrelationPanel />
            </div>
          </div>
        );
      default:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              <div className="xl:col-span-3">
                <PriceChart 
                  selectedTimeframe={selectedTimeframe}
                  activeIndicators={activeIndicators}
                  selectedAsset={selectedAsset}
                />
              </div>
              <div>
                <AIInsightsPanel />
              </div>
            </div>
            <CorrelationPanel />
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Market Analysis</h1>
              <p className="text-muted-foreground">
                Advanced charting and correlation analysis for EUA & Oil Futures
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Clock" size={16} />
                <span>Last updated: {new Date()?.toLocaleTimeString()}</span>
              </div>
              
              <Button
                variant="outline"
                iconName="Download"
                iconSize={16}
              >
                Export Analysis
              </Button>
              
              <Button
                variant="default"
                iconName="Share"
                iconSize={16}
              >
                Share Report
              </Button>
            </div>
          </div>

          {/* Asset Selection */}
          <AssetSelector
            selectedAsset={selectedAsset}
            onAssetChange={setSelectedAsset}
            selectedContract={selectedContract}
            onContractChange={setSelectedContract}
          />

          {/* View Selector */}
          <div className="flex items-center gap-2 mb-6">
            {viewOptions?.map(view => (
              <Button
                key={view?.id}
                variant={activeView === view?.id ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveView(view?.id)}
                iconName={view?.icon}
                iconPosition="left"
                iconSize={16}
                className="text-sm"
              >
                {view?.label}
              </Button>
            ))}
          </div>

          {/* Advanced Filters */}
          <AdvancedFilters
            onFiltersChange={handleFiltersChange}
            isOpen={isFiltersOpen}
            onToggle={() => setIsFiltersOpen(!isFiltersOpen)}
          />

          {/* Chart Controls - Only show in overview mode */}
          {activeView === 'overview' && (
            <ChartControls
              onTimeframeChange={setSelectedTimeframe}
              onIndicatorToggle={handleIndicatorToggle}
              onDrawingToolSelect={handleDrawingToolSelect}
              activeIndicators={activeIndicators}
              selectedTimeframe={selectedTimeframe}
            />
          )}

          {/* Main Content */}
          {renderMainContent()}

          {/* Quick Actions */}
          <div className="mt-8 p-6 bg-card border border-border rounded-lg">
            <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button
                variant="outline"
                className="h-20 flex-col gap-2"
                onClick={() => window.location.href = '/strategy-builder'}
              >
                <Icon name="Settings" size={24} />
                <span className="text-sm">Strategy Builder</span>
              </Button>
              
              <Button
                variant="outline"
                className="h-20 flex-col gap-2"
                onClick={() => window.location.href = '/real-time-order-book'}
              >
                <Icon name="BookOpen" size={24} />
                <span className="text-sm">Order Book</span>
              </Button>
              
              <Button
                variant="outline"
                className="h-20 flex-col gap-2"
                onClick={() => window.location.href = '/historical-data-explorer'}
              >
                <Icon name="Database" size={24} />
                <span className="text-sm">Historical Data</span>
              </Button>
              
              <Button
                variant="outline"
                className="h-20 flex-col gap-2"
                onClick={() => window.location.href = '/news-and-events'}
              >
                <Icon name="Newspaper" size={24} />
                <span className="text-sm">News & Events</span>
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MarketAnalysis;